// The newer built-in maps (41 of the 50): a theme each (sky, looks, scenery models), and a place in
// the difficulty curve that sets their block sizes, gaps and speeds. Each also has a couple of
// obstacles from real bhop maps (launch pads, wind, ladders, water, low gravity, spinning or blinking
// blocks, and surf ramps on the hardest ones). The scenery for the first nine maps is here too.
// The generator is in builtin.ts.

import type { Layout, Look, Module, Recipe, Scenery } from './builtin';
import { PROP_SIZES } from './propSizes';
import { columns, islands, lineProps, platformProps, scatterProps, T, type Decor } from './scenery';
import type { Shape } from './shapes';
import type { LevelTheme } from './types';

/** Scenery models whose id matches. */
const pick = (re: RegExp) => Object.keys(PROP_SIZES).filter((k) => re.test(k));

const M = {
  trees: pick(/^tree_(default|oak|fat|detailed|simple|small|tall|thin|blocks|plateau)$/),
  darkTrees: pick(/^tree_[a-z]+_dark$/),
  fallTrees: pick(/^tree_[a-z]+_fall$/),
  pines: pick(/^tree_pine(Default|Round|Tall)[A-F]$/),
  palms: [...pick(/^tree_palm/), ...pick(/^pirate-palm-/)],
  rocks: pick(/^rock_(large|tall)[A-J]$/),
  stones: pick(/^stone_tall[A-J]$/),
  flowers: pick(/^(flower_|plant_bush|grass_large|mushroom_red)/),
  mushrooms: pick(/^mushroom_/),
  cactus: pick(/^cactus_/),
  statues: pick(/^statue_/),
  lilies: pick(/^lily_/),
  space: pick(
    /^space-(craft_|hangar|machine_|meteor|rock|rocket_|rover|satelliteDish|turret_|alien|astronaut)/,
  ),
  winter: pick(/^winter-(tree|snowman|reindeer|present|candy-cane|rocks|lantern|sled|gingerbread)/),
  snowTrees: pick(/^winter-tree-snow/),
  candy: pick(/^winter-(candy-cane|present|gingerbread|nutcracker)/),
  spooky: pick(
    /^spooky-(gravestone|grave$|cross|crypt|pine|pumpkin|lightpost|fire-basket|urn|pillar|column|character|trunk)/,
  ),
  toy: pick(/^toy-/),
  city: pick(/^city-(building|low-detail)/),
  skyscrapers: pick(/^city-building-skyscraper/),
  suburb: pick(/^suburb-(building|tree|planter)/),
  urban: pick(/^urban-/),
  camp: pick(/^camp-(tent|campfire|barrel|box|signpost|tree|rock|resource|workbench)/),
  factory: pick(/^factory-/),
  cogs: pick(/^factory-cog-/),
  crystals: pick(/^crystal-((snow-)?detail-crystal|tower-round-crystals)/),
  ufos: pick(/^crystal-enemy-ufo/),
  castle: pick(/^castle-/),
  pirate: pick(/^pirate-(barrel|cannon|chest|crate|flag|rocks|tower|boat)/),
  ships: pick(/^pirate-ship-/),
  arena: pick(/^arena-/),
  // Sketchfab packs (CC BY): a forest, ancient ruins, reeds.
  forestTrees: pick(/^forest-[a-z]*-?tree-trunk/),
  forestBits: pick(/^forest-(fern|light-bush|dark-bush|mossed-stone|stone|.*stump|.*log)/),
  forestMushrooms: pick(/^forest-(fly-agaric|ceps|chanterelle|honey-mushroom)/),
  ruinsBig: pick(/^ruins-(sword|shell|rock[34])/),
  ruinsPlants: pick(/^ruins-plants/),
  ruinsRocks: pick(/^ruins-rock/),
  reeds: pick(/^reeds-/),
  ivy: pick(/^ivy-/),
};

/** Small things that stand in the platforms' corners, by theme. */
const SMALL = {
  nature: [
    ...pick(/^(flower_|mushroom_(red|tan)$|plant_bushSmall|grass_large|rock_small[A-C]$)/),
    ...M.forestMushrooms,
    ...M.ruinsPlants,
  ],
  forest: [...M.forestMushrooms, ...pick(/^forest-fern/), ...M.ruinsPlants, ...M.reeds],
  water: [...M.reeds, ...pick(/^(lily_|rock_small[A-C]$|grass_large)/)],
  toy: pick(/^(toy-(coin|flowers|mushrooms|heart|jewel|star|key)|winter-present-a-)/),
  candy: pick(/^winter-(candy-cane|present-|gingerbread)/),
  beach: pick(/^(camp-(barrel|box$|bucket)|rock_small[A-C]$|cactus_short|grass_large)/),
  desert: [...pick(/^(cactus_short|rock_small[A-F]$|pot_small)/), ...M.ruinsRocks, ...M.ruinsPlants],
  winter: pick(/^(winter-(present-|lantern$|candy-cane)|rock_small[A-C]$)/),
  spooky: pick(/^spooky-(candle|lantern-candle|pumpkin|urn-|gravestone-(round|cross$|bevel))/),
  city: pick(/^(factory-(cone|box-small)|camp-box$|urban-detail-light-single|urban-pallet-small)/),
  factory: pick(/^factory-(cone|box-small|box-wide|button-floor|lever)/),
  space: pick(/^(space-(barrel|rocks_small|rock$)|crystal-detail-crystal$)/),
  crystal: pick(/^crystal-(snow-)?detail-crystal$/),
  castle: pick(/^(castle-flag(-pennant)?$|arena-banner|camp-barrel|rock_small[A-C]$)/),
  ruins: [...M.ruinsPlants, ...M.ruinsRocks, ...pick(/^statue_head|pot_small/)],
};

/** What a theme puts beside the course: floating islands and their models, and the platforms' corners. */
interface Level {
  island: Look;
  shape?: Shape;
  on: readonly string[];
  onScale: [number, number];
  small: readonly string[];
  smallScale: [number, number];
  count?: number;
}

const GRASS: Look = ['img:surface-grass', '#ffffff'];
const LEVELS: Record<string, Level> = {
  meadow: {
    island: GRASS,
    on: [...M.trees, ...M.forestTrees, ...M.forestBits],
    onScale: [200, 340],
    small: SMALL.nature,
    smallScale: [280, 420],
  },
  candy: {
    island: ['tiles', '#ffd1e8'],
    shape: 'cylinder',
    on: [...M.candy, ...M.toy],
    onScale: [160, 300],
    small: SMALL.candy,
    smallScale: [120, 200],
  },
  beach: {
    island: ['sand', '#e8d39a'],
    on: [...M.palms, ...M.pirate],
    onScale: [150, 300],
    small: SMALL.beach,
    smallScale: [220, 320],
  },
  autumn: {
    island: ['img:surface-forest', '#ffffff'],
    on: [...M.fallTrees, ...M.forestBits],
    onScale: [220, 360],
    small: SMALL.forest,
    smallScale: [300, 460],
  },
  pond: {
    island: ['img:surface-moss', '#ffffff'],
    on: [...M.trees, ...M.reeds, ...M.forestBits],
    onScale: [180, 320],
    small: SMALL.water,
    smallScale: [120, 240],
  },
  mushroom: {
    island: ['img:surface-forest', '#ffffff'],
    on: [...M.mushrooms, ...M.forestTrees, ...M.forestMushrooms],
    onScale: [300, 600],
    small: SMALL.forest,
    smallScale: [340, 520],
  },
  toy: {
    island: ['tiles', '#7ce06b'],
    shape: 'cylinder',
    on: M.toy,
    onScale: [180, 320],
    small: SMALL.toy,
    smallScale: [160, 260],
  },
  village: {
    island: GRASS,
    on: [...M.suburb, ...M.trees],
    onScale: [160, 260],
    small: SMALL.nature,
    smallScale: [280, 420],
  },
  desert: {
    island: ['sand', '#e2c07a'],
    on: [...M.cactus, ...M.ruinsBig, ...M.statues],
    onScale: [160, 300],
    small: SMALL.desert,
    smallScale: [220, 340],
  },
  harbor: {
    island: ['img:surface-woodfloor', '#ffffff'],
    shape: 'box',
    on: [...M.pirate, ...M.palms],
    onScale: [100, 220],
    small: SMALL.beach,
    smallScale: [220, 320],
  },
  pine: {
    island: ['img:surface-forest', '#ffffff'],
    on: [...M.pines, ...M.forestTrees, ...M.forestBits],
    onScale: [200, 340],
    small: SMALL.forest,
    smallScale: [300, 460],
  },
  camp: {
    island: ['img:surface-dirt', '#ffffff'],
    on: [...M.camp, ...M.pines, ...M.forestBits],
    onScale: [160, 300],
    small: SMALL.forest,
    smallScale: [300, 460],
  },
  urban: {
    island: ['img:surface-concrete', '#ffffff'],
    shape: 'box',
    on: M.urban,
    onScale: [150, 260],
    small: SMALL.city,
    smallScale: [120, 220],
  },
  castle: {
    island: ['img:surface-rock', '#ffffff'],
    on: [...M.castle, ...M.trees],
    onScale: [140, 260],
    small: SMALL.castle,
    smallScale: [120, 260],
  },
  arena: {
    island: ['img:surface-marble', '#ffffff'],
    on: [...M.arena, ...M.statues, ...M.ruinsBig],
    onScale: [160, 300],
    small: SMALL.ruins,
    smallScale: [220, 340],
  },
  factory: {
    island: ['img:surface-metalplates', '#ffffff'],
    shape: 'box',
    on: M.factory,
    onScale: [140, 260],
    small: SMALL.factory,
    smallScale: [120, 220],
  },
  clockwork: {
    island: ['img:surface-metalplates', '#ffffff'],
    shape: 'hex',
    on: [...M.cogs, ...M.factory],
    onScale: [160, 320],
    small: SMALL.factory,
    smallScale: [120, 220],
  },
  spooky: {
    island: ['img:surface-gravel', '#ffffff'],
    on: M.spooky,
    onScale: [160, 300],
    small: SMALL.spooky,
    smallScale: [220, 320],
  },
  winter: {
    island: ['img:surface-snow', '#ffffff'],
    on: [...M.snowTrees, ...M.winter],
    onScale: [160, 280],
    small: SMALL.winter,
    smallScale: [120, 240],
  },
  crystal: {
    island: ['img:surface-cliff', '#ffffff'],
    on: [...M.crystals, ...M.ruinsRocks],
    onScale: [300, 600],
    small: SMALL.crystal,
    smallScale: [220, 300],
  },
  skyline: {
    island: ['img:surface-concrete', '#ffffff'],
    shape: 'box',
    on: [...M.city, ...M.urban],
    onScale: [120, 220],
    small: SMALL.city,
    smallScale: [120, 220],
  },
  ruins: {
    island: ['img:surface-marble', '#ffffff'],
    on: [...M.ruinsBig, ...M.statues, ...M.stones, ...M.ivy],
    onScale: [160, 300],
    small: SMALL.ruins,
    smallScale: [220, 340],
  },
  tropics: {
    island: ['sand', '#ecd8a0'],
    on: [...M.palms, ...M.flowers],
    onScale: [180, 340],
    small: SMALL.beach,
    smallScale: [220, 320],
  },
  space: {
    island: ['img:surface-gravel', '#a8a8b8'],
    on: M.space,
    onScale: [120, 240],
    small: SMALL.space,
    smallScale: [160, 300],
  },
  ghost: {
    island: ['img:surface-woodfloor', '#8a9a8a'],
    shape: 'box',
    on: [...M.spooky, ...M.pirate],
    onScale: [120, 240],
    small: SMALL.spooky,
    smallScale: [220, 320],
  },
  storm: {
    island: ['img:surface-cliff', '#ffffff'],
    on: [...M.stones, ...M.rocks, ...M.pines],
    onScale: [180, 360],
    small: SMALL.desert,
    smallScale: [220, 340],
  },
  sky: {
    island: GRASS,
    on: [...M.trees, ...M.forestTrees, ...M.ruinsBig, ...M.flowers],
    onScale: [200, 340],
    small: SMALL.nature,
    smallScale: [280, 420],
    count: 22,
  },
  volcano: {
    island: ['img:surface-rock', '#8a6a5a'],
    on: [...M.rocks, ...M.stones, ...M.ruinsBig],
    onScale: [200, 400],
    small: SMALL.desert,
    smallScale: [220, 340],
  },
  abyss: {
    island: ['img:surface-cliff', '#6a7088'],
    on: [...M.crystals, ...M.darkTrees],
    onScale: [260, 520],
    small: SMALL.crystal,
    smallScale: [220, 300],
  },
  neon: {
    island: ['metal', '#2a3040'],
    shape: 'box',
    on: [...M.skyscrapers, ...M.factory],
    onScale: [100, 200],
    small: SMALL.city,
    smallScale: [120, 220],
  },
};

/** A theme's floor scenery plus its level scenery: islands beside the course, things on the platforms. */
function withLevel(floor: (d: Decor) => void, key: string): (d: Decor) => void {
  const l = LEVELS[key];
  return (d) => {
    floor(d);
    if (!l) return;
    islands(d, l.count ?? 16, l.island, l.on, l.onScale, l.shape);
    platformProps(d, l.small, l.smallScale);
  };
}

/* ---------------- Scenery for each theme ---------------- */

type Decorate = (d: Decor) => void;

const meadow: Decorate = (d) => {
  scatterProps(d, M.trees, 90, [260, 480]);
  scatterProps(d, M.flowers, 70, [180, 320], 1600);
  scatterProps(d, M.rocks, 25, [160, 320]);
};
const DECOR: Record<string, Decorate> = {
  meadow,
  candy: (d) => {
    scatterProps(d, M.candy, 60, [180, 360]);
    scatterProps(d, M.toy, 60, [180, 320]);
  },
  beach: (d) => {
    scatterProps(d, M.palms, 70, [220, 420]);
    scatterProps(d, M.pirate, 30, [120, 220]);
    scatterProps(d, M.ships, 4, [45, 70], 4200);
  },
  autumn: (d) => {
    scatterProps(d, M.fallTrees, 100, [260, 480]);
    scatterProps(d, M.mushrooms, 40, [180, 300], 1600);
  },
  pond: (d) => {
    scatterProps(d, M.lilies, 60, [260, 420], 1800);
    scatterProps(d, M.trees, 60, [260, 460]);
    scatterProps(d, M.rocks, 20, [140, 260]);
  },
  mushroom: (d) => {
    scatterProps(d, M.mushrooms, 120, [260, 520]);
    scatterProps(d, M.darkTrees, 40, [240, 420]);
  },
  toy: (d) => {
    scatterProps(d, M.toy, 120, [180, 340]);
  },
  village: (d) => {
    scatterProps(d, M.suburb, 70, [180, 300]);
    scatterProps(d, M.trees, 40, [240, 420]);
  },
  desert: (d) => {
    scatterProps(d, M.cactus, 70, [260, 460]);
    scatterProps(d, M.statues, 25, [220, 400]);
    scatterProps(d, M.rocks, 40, [180, 380]);
  },
  harbor: (d) => {
    scatterProps(d, M.ships, 10, [40, 70], 4200);
    scatterProps(d, M.pirate, 60, [140, 260]);
    scatterProps(d, M.palms, 20, [240, 420]);
  },
  pine: (d) => {
    scatterProps(d, M.pines, 130, [280, 520]);
    scatterProps(d, M.rocks, 30, [180, 360]);
  },
  camp: (d) => {
    scatterProps(d, M.camp, 70, [160, 280]);
    scatterProps(d, M.pines, 60, [280, 480]);
  },
  urban: (d) => {
    scatterProps(d, M.urban, 70, [160, 280]);
    scatterProps(d, M.city, 30, [160, 280], 3600);
  },
  castle: (d) => {
    scatterProps(d, M.castle, 70, [160, 300]);
    scatterProps(d, M.trees, 30, [260, 440]);
  },
  arena: (d) => {
    scatterProps(d, M.arena, 50, [180, 320]);
    scatterProps(d, M.statues, 40, [200, 380]);
  },
  factory: (d) => {
    scatterProps(d, M.factory, 110, [140, 280]);
  },
  clockwork: (d) => {
    scatterProps(d, M.cogs, 50, [260, 520]);
    scatterProps(d, M.factory, 60, [140, 260]);
  },
  spooky: (d) => {
    scatterProps(d, M.spooky, 120, [160, 320]);
  },
  winter: (d) => {
    scatterProps(d, M.snowTrees, 90, [220, 380]);
    scatterProps(d, M.winter, 50, [160, 280]);
  },
  crystal: (d) => {
    scatterProps(d, M.crystals, 90, [380, 760]);
    scatterProps(d, M.rocks, 30, [200, 400]);
  },
  skyline: (d) => {
    scatterProps(d, M.skyscrapers, 50, [220, 360], 4200);
    scatterProps(d, M.city, 60, [180, 300], 3600);
  },
  ruins: (d) => {
    scatterProps(d, M.statues, 70, [240, 440]);
    scatterProps(d, M.stones, 50, [200, 380]);
  },
  tropics: (d) => {
    scatterProps(d, M.palms, 100, [240, 440]);
    scatterProps(d, M.flowers, 40, [180, 300], 1600);
  },
  space: (d) => {
    scatterProps(d, M.space, 110, [160, 320]);
    scatterProps(d, M.ufos, 10, [260, 420], 3600);
  },
  ghost: (d) => {
    scatterProps(d, M.ships, 8, [45, 75], 4200);
    scatterProps(d, M.spooky, 60, [160, 300]);
  },
  storm: (d) => {
    scatterProps(d, M.stones, 80, [260, 520]);
    scatterProps(d, M.rocks, 60, [220, 460]);
  },
  sky: (d) => {
    scatterProps(d, M.trees, 70, [260, 460]);
    scatterProps(d, M.stones, 40, [240, 480]);
    scatterProps(d, M.flowers, 40, [180, 300], 1600);
  },
  volcano: (d) => {
    scatterProps(d, M.rocks, 90, [260, 560]);
    scatterProps(d, M.stones, 50, [260, 520]);
  },
  abyss: (d) => {
    scatterProps(d, M.crystals, 80, [380, 820]);
    scatterProps(d, M.darkTrees, 30, [240, 420]);
  },
  neon: (d) => {
    scatterProps(d, M.skyscrapers, 60, [220, 380], 4200);
    scatterProps(d, M.factory, 40, [140, 260]);
  },
};

for (const k of Object.keys(DECOR)) DECOR[k] = withLevel(DECOR[k], k);

/** Scenery models for the first nine maps (their courses don't change). */
export const OLD_DECOR: Record<string, Decorate> = {
  bhop_meadow: (d) => {
    meadow(d);
    lineProps(d, M.flowers, 9, 700, [160, 260]);
  },
  bhop_toybox: DECOR.toy,
  bhop_canyon: (d) => {
    scatterProps(d, M.cactus, 50, [260, 460]);
    scatterProps(d, M.rocks, 40, [220, 420]);
  },
  bhop_ocean: (d) => {
    scatterProps(d, M.ships, 8, [45, 70], 4200);
    scatterProps(d, M.palms, 30, [240, 420]);
    scatterProps(d, M.pirate, 30, [140, 240]);
  },
  bhop_neon: DECOR.skyline,
  bhop_jungle: (d) => {
    scatterProps(d, M.palms, 60, [240, 460]);
    scatterProps(d, M.statues, 25, [220, 420]);
    scatterProps(d, M.darkTrees, 40, [260, 480]);
  },
  bhop_frost: DECOR.winter,
  bhop_station: DECOR.space,
  bhop_inferno: (d) => {
    scatterProps(d, M.spooky, 60, [160, 320]);
    scatterProps(d, M.rocks, 50, [240, 520]);
  },
};

/** The first nine maps' level scenery, by the theme closest to each. */
const OLD_LEVEL: Record<string, string> = {
  bhop_meadow: 'meadow',
  bhop_toybox: 'toy',
  bhop_canyon: 'desert',
  bhop_ocean: 'tropics',
  bhop_neon: 'neon',
  bhop_jungle: 'ruins',
  bhop_frost: 'winter',
  bhop_station: 'space',
  bhop_inferno: 'volcano',
};
for (const [name, key] of Object.entries(OLD_LEVEL)) {
  const floor = OLD_DECOR[name];
  // Their floor scenery is already wrapped when it's a theme's; add only what's missing.
  const theme = Object.entries(DECOR).find(([, f]) => f === floor);
  if (!theme) OLD_DECOR[name] = withLevel(floor, key);
}

/* ---------------- The maps ---------------- */

interface Spec {
  name: string;
  title: string;
  tier: number;
  stages: number;
  theme: Partial<LevelTheme>;
  start: Look;
  checkpoint: Look;
  end: Look;
  blocks: Look[];
  solid: Look;
  layout?: Layout;
  /** Obstacles from real bhop maps this map features. */
  extra: Module[];
  decor: keyof typeof DECOR;
}

const BASE: Module[][] = [
  [],
  ['line', 'zigzag', 'curve', 'shapes', 'stairs', 'window', 'ramp'],
  [
    'zigzag',
    'line',
    'stairs',
    'drop',
    'shapes',
    'pillars',
    'movers',
    'curve',
    'window',
    'elevator',
    'longjump',
  ],
  [
    'zigzag',
    'line',
    'movers',
    'shapes',
    'window',
    'drop',
    'curve',
    'pillars',
    'elevator',
    'boost',
    'longjump',
  ],
  [
    'zigzag',
    'shapes',
    'movers',
    'drop',
    'window',
    'pillars',
    'curve',
    'elevator',
    'boost',
    'line',
    'ramp',
    'longjump',
  ],
  [
    'shapes',
    'zigzag',
    'movers',
    'drop',
    'pillars',
    'window',
    'elevator',
    'boost',
    'curve',
    'ramp',
    'longjump',
  ],
];

/**
 * The map's modules: its own obstacles mixed in early, so they show up from the first stages (surf
 * ramps a little later: a few per map, after you've warmed up).
 */
function modules(tier: number, extra: Module[]): Module[] {
  const base = BASE[tier].filter((m) => !extra.includes(m));
  if (extra[0] === 'surf') extra = [...extra.slice(1), 'surf'];
  const out: Module[] = [base[0]];
  for (let i = 0; i < Math.max(extra.length, base.length - 1); i++) {
    if (i < extra.length) out.push(extra[i]);
    if (i + 1 < base.length) out.push(base[i + 1]);
  }
  return out;
}

const W = '#ffffff';
const SPECS: Spec[] = [
  // ★ Easy
  {
    name: 'bhop_sunny',
    title: 'Sunny Fields',
    tier: 1,
    stages: 10,
    theme: { voidColor: '#5c9a3e' },
    start: ['img:surface-grass', W],
    checkpoint: ['planks', '#b98a54'],
    end: ['img:surface-woodfloor', W],
    blocks: [
      ['grass', '#6fbf4a'],
      ['planks', '#c58c4e'],
    ],
    solid: ['img:surface-rock', W],
    layout: 'arc',
    extra: ['pads', 'spinners'],
    decor: 'meadow',
  },
  {
    name: 'bhop_candy',
    title: 'Candy Hop',
    tier: 1,
    stages: 11,
    theme: { skyTop: '#ff9ad5', skyHorizon: '#fff1f8', fog: '#ffe6f2', voidColor: '#ffd1e8' },
    start: ['tiles', '#ffffff'],
    checkpoint: ['tiles', '#ffb3d9'],
    end: ['tiles', '#ff7aa8'],
    blocks: [
      ['tiles', '#ff6f91'],
      ['tiles', '#7ce0d0'],
      ['tiles', '#ffe36e'],
    ],
    solid: ['tiles', '#f4f4f4'],
    layout: 'zig',
    extra: ['pads'],
    decor: 'candy',
  },
  {
    name: 'bhop_beach',
    title: 'Beach Day',
    tier: 1,
    stages: 11,
    theme: { skyTop: '#2f8fe0', skyHorizon: '#d8f4ff', fog: '#c8eefc', voidColor: '#e8d39a' },
    start: ['img:surface-woodfloor', W],
    checkpoint: ['sand', '#e7c48c'],
    end: ['img:surface-woodfloor', W],
    blocks: [
      ['sand', '#e2c07a'],
      ['planks', '#c99a5e'],
    ],
    solid: ['img:surface-rock', W],
    extra: ['swim', 'spinners'],
    decor: 'beach',
  },
  {
    name: 'bhop_autumn',
    title: 'Autumn Trail',
    tier: 1,
    stages: 12,
    theme: { skyTop: '#d98a4a', skyHorizon: '#ffe7c4', fog: '#f5d6b0', voidColor: '#8a5a2a' },
    start: ['img:surface-forest', W],
    checkpoint: ['planks', '#a7794a'],
    end: ['img:surface-woodfloor', W],
    blocks: [
      ['planks', '#c76a2c'],
      ['grass', '#c8a040'],
      ['planks', '#9a5a2a'],
    ],
    solid: ['img:surface-bark', W],
    layout: 'arc',
    extra: ['spinners', 'ladder'],
    decor: 'autumn',
  },
  {
    name: 'bhop_pond',
    title: 'Lily Pond',
    tier: 1,
    stages: 13,
    theme: { skyTop: '#4a9ad8', skyHorizon: '#dff3ff', fog: '#cfe9f5', voidColor: '#2f7a8a' },
    start: ['img:surface-moss', W],
    checkpoint: ['stone', '#9aa08a'],
    end: ['img:surface-moss', W],
    blocks: [
      ['grass', '#5fae3e'],
      ['stone', '#a8b09a'],
    ],
    solid: ['img:surface-rock', W],
    extra: ['swim', 'pads'],
    decor: 'pond',
  },
  {
    name: 'bhop_mushroom',
    title: 'Mushroom Grove',
    tier: 1,
    stages: 13,
    theme: { skyTop: '#5a4a8a', skyHorizon: '#e0c8ff', fog: '#cdb8ea', voidColor: '#3a2e5a' },
    start: ['img:surface-forest', W],
    checkpoint: ['grass', '#7a9a5a'],
    end: ['img:surface-moss', W],
    blocks: [
      ['tiles', '#d0503a'],
      ['tiles', '#e8d6b0'],
    ],
    solid: ['img:surface-bark', W],
    layout: 'zig',
    extra: ['ladder', 'pads'],
    decor: 'mushroom',
  },
  {
    name: 'bhop_playground',
    title: 'Playground',
    tier: 1,
    stages: 14,
    theme: { skyTop: '#59b8ff', skyHorizon: '#fff6d8', fog: '#e8f6ff', voidColor: '#7cc85a' },
    start: ['tiles', '#ffffff'],
    checkpoint: ['tiles', '#ffe36e'],
    end: ['tiles', '#7ce06b'],
    blocks: [
      ['tiles', '#ff6f91'],
      ['tiles', '#59c3ff'],
      ['tiles', '#ffb84d'],
      ['tiles', '#b18cff'],
    ],
    solid: ['tiles', '#e8e8e8'],
    extra: ['spinners', 'pads'],
    decor: 'toy',
  },
  {
    name: 'bhop_village',
    title: 'Little Village',
    tier: 1,
    stages: 14,
    theme: { voidColor: '#6a9a4a' },
    start: ['img:surface-paving', W],
    checkpoint: ['bricks', '#b4643a'],
    end: ['img:surface-paving', W],
    blocks: [
      ['bricks', '#c06a44'],
      ['planks', '#c58c4e'],
    ],
    solid: ['img:surface-concrete', W],
    layout: 'arc',
    extra: ['ladder', 'swim'],
    decor: 'village',
  },
  // ★★ Medium
  {
    name: 'bhop_desert',
    title: 'Desert Ruins',
    tier: 2,
    stages: 14,
    theme: { skyTop: '#3a7ad0', skyHorizon: '#ffe2b0', fog: '#f2d4a2', voidColor: '#d8b070' },
    start: ['sand', '#e2c07a'],
    checkpoint: ['img:surface-marble', W],
    end: ['img:surface-marble', W],
    blocks: [
      ['sand', '#d8a768'],
      ['stone', '#c9a87a'],
    ],
    solid: ['stone', '#b8946a'],
    layout: 'zig',
    extra: ['windgap', 'ladder'],
    decor: 'desert',
  },
  {
    name: 'bhop_harbor',
    title: 'Harbor',
    tier: 2,
    stages: 15,
    theme: { skyTop: '#2d6fb0', skyHorizon: '#c8e6f5', fog: '#b8dcec', voidColor: '#1f5a80' },
    start: ['img:surface-woodfloor', W],
    checkpoint: ['planks', '#8a6238'],
    end: ['img:surface-woodfloor', W],
    blocks: [
      ['planks', '#a87444'],
      ['metal', '#8a95a8'],
    ],
    solid: ['img:surface-paintedmetal', W],
    extra: ['swim', 'windgap'],
    decor: 'harbor',
  },
  {
    name: 'bhop_pines',
    title: 'Pine Ridge',
    tier: 2,
    stages: 15,
    theme: { skyTop: '#4a7aa8', skyHorizon: '#dfeefa', fog: '#d0e2ee', voidColor: '#2f5a3a' },
    start: ['img:surface-forest', W],
    checkpoint: ['planks', '#7a5230'],
    end: ['img:surface-forest', W],
    blocks: [
      ['grass', '#3f8f2e'],
      ['planks', '#8a6238'],
    ],
    solid: ['img:surface-cliff', W],
    layout: 'arc',
    extra: ['ladder', 'lowgrav'],
    decor: 'pine',
  },
  {
    name: 'bhop_camp',
    title: 'Camp Lake',
    tier: 2,
    stages: 16,
    theme: { skyTop: '#3a5a9a', skyHorizon: '#f5d8b8', fog: '#e8ccb0', voidColor: '#3a6a5a' },
    start: ['img:surface-dirt', W],
    checkpoint: ['planks', '#9a6f40'],
    end: ['img:surface-dirt', W],
    blocks: [
      ['planks', '#b4844a'],
      ['grass', '#5a9a3a'],
    ],
    solid: ['img:surface-rock', W],
    extra: ['swim', 'spinners'],
    decor: 'camp',
  },
  {
    name: 'bhop_oldtown',
    title: 'Old Town',
    tier: 2,
    stages: 16,
    theme: { skyTop: '#6a7a8a', skyHorizon: '#e8e2d8', fog: '#d8d2c8', voidColor: '#5a5650' },
    start: ['img:surface-paving', W],
    checkpoint: ['bricks', '#8a6a5a'],
    end: ['img:surface-paving', W],
    blocks: [
      ['bricks', '#a05a3a'],
      ['concrete', '#9a9a92'],
    ],
    solid: ['img:surface-concrete', W],
    layout: 'zig',
    extra: ['ladder', 'windgap'],
    decor: 'urban',
  },
  {
    name: 'bhop_castle',
    title: 'Castle Walls',
    tier: 2,
    stages: 17,
    theme: { skyTop: '#4a6aa0', skyHorizon: '#e6ecf5', fog: '#d6dde8', voidColor: '#4a6a3a' },
    start: ['stone', '#9a9a8a'],
    checkpoint: ['bricks', '#8a8478'],
    end: ['stone', '#b8b09a'],
    blocks: [
      ['stone', '#a8a090'],
      ['bricks', '#9a8a78'],
    ],
    solid: ['img:surface-rock', W],
    extra: ['ladder', 'pads'],
    decor: 'castle',
  },
  {
    name: 'bhop_flowers',
    title: 'Flower Valley',
    tier: 2,
    stages: 17,
    theme: { skyTop: '#6aa8e8', skyHorizon: '#fff0f6', fog: '#f5e2ee', voidColor: '#6aaa4a' },
    start: ['img:surface-grass', W],
    checkpoint: ['grass', '#7ac24a'],
    end: ['img:surface-grass', W],
    blocks: [
      ['tiles', '#e04f9f'],
      ['tiles', '#ffd23a'],
      ['tiles', '#9b6fe0'],
    ],
    solid: ['img:surface-moss', W],
    layout: 'arc',
    extra: ['lowgrav', 'pads'],
    decor: 'meadow',
  },
  {
    name: 'bhop_colosseum',
    title: 'Colosseum',
    tier: 2,
    stages: 18,
    theme: { skyTop: '#3a7ad0', skyHorizon: '#fbe8c8', fog: '#eedcbc', voidColor: '#c8a878' },
    start: ['img:surface-marble', W],
    checkpoint: ['stone', '#d8ccb0'],
    end: ['img:surface-marble', W],
    blocks: [
      ['stone', '#e0d4b8'],
      ['tiles', '#c8b088'],
    ],
    solid: ['img:surface-marble', W],
    extra: ['spinners', 'windgap'],
    decor: 'arena',
  },
  // ★★★ Hard
  {
    name: 'bhop_factory',
    title: 'Factory',
    tier: 3,
    stages: 18,
    theme: { skyTop: '#3a4250', skyHorizon: '#a8b0b8', fog: '#8a929a', voidColor: '#2a2e34' },
    start: ['img:surface-metalplates', W],
    checkpoint: ['metal', '#6a7280'],
    end: ['img:surface-metalplates', W],
    blocks: [
      ['metal', '#ffb03a'],
      ['metal', '#8a95ae'],
    ],
    solid: ['img:surface-paintedmetal', W],
    layout: 'zig',
    extra: ['blinkers', 'spinners'],
    decor: 'factory',
  },
  {
    name: 'bhop_graveyard',
    title: 'Graveyard',
    tier: 3,
    stages: 19,
    theme: { skyTop: '#0e1422', skyHorizon: '#4a3a5a', fog: '#2a2436', ambient: 0.5, voidColor: '#1a1a22' },
    start: ['img:surface-gravel', W],
    checkpoint: ['stone', '#6a6a72'],
    end: ['img:surface-gravel', W],
    blocks: [
      ['stone', '#8a8a92'],
      ['bricks', '#6a5a5a'],
    ],
    solid: ['img:surface-cliff', W],
    extra: ['blinkers', 'updraft'],
    decor: 'spooky',
  },
  {
    name: 'bhop_snowtown',
    title: 'Snowy Village',
    tier: 3,
    stages: 19,
    theme: { skyTop: '#8fb8e0', skyHorizon: '#f2f8ff', fog: '#e8f0fa', ambient: 0.65, voidColor: '#e8eef5' },
    start: ['img:surface-snow', W],
    checkpoint: ['planks', '#8a6238'],
    end: ['img:surface-snow', W],
    blocks: [
      ['ice', '#9fd0ff'],
      ['planks', '#a87444'],
    ],
    solid: ['img:surface-snow', W],
    layout: 'arc',
    extra: ['pads', 'lowgrav'],
    decor: 'winter',
  },
  {
    name: 'bhop_crystal',
    title: 'Crystal Caves',
    tier: 3,
    stages: 20,
    theme: { skyTop: '#140a2a', skyHorizon: '#4a2a7a', fog: '#2a1a4a', ambient: 0.55, voidColor: '#1a0f30' },
    start: ['img:surface-cliff', W],
    checkpoint: ['crystal', '#9b5cff'],
    end: ['crystal', '#21e1ff'],
    blocks: [
      ['crystal', '#b18cff'],
      ['crystal', '#5ad1c8'],
    ],
    solid: ['img:surface-rock', W],
    extra: ['updraft', 'blinkers'],
    decor: 'crystal',
  },
  {
    name: 'bhop_skyline',
    title: 'Skyline',
    tier: 3,
    stages: 20,
    theme: { skyTop: '#ff8a5a', skyHorizon: '#ffe0b8', fog: '#f0c8a0', voidColor: '#5a5a6a' },
    start: ['img:surface-concrete', W],
    checkpoint: ['metal', '#7a8494'],
    end: ['img:surface-concrete', W],
    blocks: [
      ['glass', '#6fd6ff'],
      ['metal', '#a8b0c0'],
    ],
    solid: ['img:surface-concrete', W],
    layout: 'zig',
    extra: ['windgap', 'pads'],
    decor: 'skyline',
  },
  {
    name: 'bhop_rooftops',
    title: 'Rooftops',
    tier: 3,
    stages: 21,
    theme: { skyTop: '#1a2a4a', skyHorizon: '#c87a5a', fog: '#7a5a5a', voidColor: '#2a2a32' },
    start: ['img:surface-concrete', W],
    checkpoint: ['bricks', '#8a5a4a'],
    end: ['img:surface-concrete', W],
    blocks: [
      ['bricks', '#a05a3a'],
      ['metal', '#7a8494'],
    ],
    solid: ['img:surface-paving', W],
    extra: ['ladder', 'windgap'],
    decor: 'urban',
  },
  {
    name: 'bhop_ruins',
    title: 'Sky Ruins',
    tier: 3,
    stages: 21,
    theme: { skyTop: '#4a8ad8', skyHorizon: '#f8f0e0', fog: '#e8e0d0', voidColor: '#8aa0b8' },
    start: ['img:surface-marble', W],
    checkpoint: ['stone', '#c8c0a8'],
    end: ['img:surface-marble', W],
    blocks: [
      ['stone', '#d8d0b8'],
      ['crystal', '#7fe0ff'],
    ],
    solid: ['img:surface-marble', W],
    layout: 'arc',
    extra: ['updraft', 'spinners'],
    decor: 'ruins',
  },
  {
    name: 'bhop_tropics',
    title: 'Tropics',
    tier: 3,
    stages: 22,
    theme: { skyTop: '#1a8ad8', skyHorizon: '#c8fff0', fog: '#b0ece0', voidColor: '#1a8a9a' },
    start: ['img:surface-woodfloor', W],
    checkpoint: ['sand', '#e7c48c'],
    end: ['img:surface-woodfloor', W],
    blocks: [
      ['grass', '#4fbf5a'],
      ['planks', '#c99a5e'],
    ],
    solid: ['img:surface-rock', W],
    extra: ['swim', 'lowgrav'],
    decor: 'tropics',
  },
  // ★★★★ Expert
  {
    name: 'bhop_glacier',
    title: 'Glacier',
    tier: 4,
    stages: 22,
    theme: { skyTop: '#a8c8e8', skyHorizon: '#f5faff', fog: '#e4efff', ambient: 0.65, voidColor: '#d0e2f2' },
    start: ['img:surface-ice', W],
    checkpoint: ['ice', '#a9d6ff'],
    end: ['img:surface-snow', W],
    blocks: [
      ['ice', '#9fd0ff'],
      ['crystal', '#7fb8ff'],
    ],
    solid: ['img:surface-ice', W],
    layout: 'zig',
    extra: ['blinkers', 'lowgrav'],
    decor: 'winter',
  },
  {
    name: 'bhop_moon',
    title: 'Moon Base',
    tier: 4,
    stages: 23,
    theme: { skyTop: '#000004', skyHorizon: '#1a1a2a', fog: '#0a0a14', ambient: 0.5, voidColor: '#8a8a8a' },
    start: ['img:surface-metalplates', W],
    checkpoint: ['metal', '#7a8494'],
    end: ['neon', '#3affb0'],
    blocks: [
      ['metal', '#c0c8d6'],
      ['neon', '#3affb0'],
    ],
    solid: ['img:surface-gravel', W],
    extra: ['lowgrav', 'pads'],
    decor: 'space',
  },
  {
    name: 'bhop_clockwork',
    title: 'Clockwork',
    tier: 4,
    stages: 23,
    theme: { skyTop: '#3a2a1a', skyHorizon: '#c8a060', fog: '#8a6a3a', voidColor: '#2a1e14' },
    start: ['img:surface-metalplates', W],
    checkpoint: ['metal', '#a08050'],
    end: ['metal', '#e0b050'],
    blocks: [
      ['metal', '#c89a50'],
      ['metal', '#8a7a6a'],
    ],
    solid: ['img:surface-paintedmetal', W],
    layout: 'arc',
    extra: ['spinners', 'blinkers'],
    decor: 'clockwork',
  },
  {
    name: 'bhop_ghostship',
    title: 'Ghost Ship',
    tier: 4,
    stages: 24,
    theme: { skyTop: '#0a1a1a', skyHorizon: '#3a6a5a', fog: '#1a3a32', ambient: 0.5, voidColor: '#0a2a2a' },
    start: ['img:surface-woodfloor', '#8a9a8a'],
    checkpoint: ['planks', '#5a6a5a'],
    end: ['img:surface-woodfloor', '#9aaa9a'],
    blocks: [
      ['planks', '#6a7a6a'],
      ['crystal', '#5ad1a8'],
    ],
    solid: ['img:surface-bark', W],
    extra: ['updraft', 'windgap'],
    decor: 'ghost',
  },
  {
    name: 'bhop_pyramid',
    title: 'Pyramid',
    tier: 4,
    stages: 24,
    theme: { skyTop: '#2a6ac0', skyHorizon: '#ffd890', fog: '#f0c880', voidColor: '#d0a058' },
    start: ['sand', '#e2c07a'],
    checkpoint: ['img:surface-marble', W],
    end: ['crystal', '#e8d36a'],
    blocks: [
      ['sand', '#d8a768'],
      ['bricks', '#c8a070'],
    ],
    solid: ['stone', '#c8a070'],
    layout: 'zig',
    extra: ['ladder', 'blinkers'],
    decor: 'desert',
  },
  {
    name: 'bhop_storm',
    title: 'Storm Peaks',
    tier: 4,
    stages: 25,
    theme: { skyTop: '#2a3240', skyHorizon: '#8a94a0', fog: '#6a727e', ambient: 0.55, voidColor: '#3a4048' },
    start: ['img:surface-cliff', W],
    checkpoint: ['stone', '#7a828a'],
    end: ['img:surface-cliff', W],
    blocks: [
      ['stone', '#9aa2aa'],
      ['ice', '#c2e4ff'],
    ],
    solid: ['img:surface-rock', W],
    extra: ['windgap', 'updraft'],
    decor: 'storm',
  },
  {
    name: 'bhop_skyislands',
    title: 'Sky Islands',
    tier: 4,
    stages: 25,
    theme: { skyTop: '#3a8ae0', skyHorizon: '#e8f6ff', fog: '#d8ecfa', voidColor: '#9ac8f0' },
    start: ['img:surface-grass', W],
    checkpoint: ['grass', '#5fae3e'],
    end: ['img:surface-grass', W],
    blocks: [
      ['grass', '#78c24a'],
      ['stone', '#a8a898'],
    ],
    solid: ['img:surface-cliff', W],
    layout: 'arc',
    extra: ['pads', 'updraft'],
    decor: 'sky',
  },
  {
    name: 'bhop_fortress',
    title: 'Fortress',
    tier: 4,
    stages: 26,
    theme: { skyTop: '#3a4a6a', skyHorizon: '#c8ccd8', fog: '#a8acb8', voidColor: '#3a4a3a' },
    start: ['stone', '#8a8a82'],
    checkpoint: ['bricks', '#7a7468'],
    end: ['stone', '#a8a090'],
    blocks: [
      ['stone', '#9a9488'],
      ['metal', '#6a6e78'],
    ],
    solid: ['img:surface-rock', W],
    extra: ['spinners', 'ladder'],
    decor: 'castle',
  },
  // ★★★★★ Insane (surf ramps)
  {
    name: 'bhop_volcano',
    title: 'Volcano',
    tier: 5,
    stages: 27,
    theme: { skyTop: '#1a0604', skyHorizon: '#8a2a0a', fog: '#4a1206', ambient: 0.5, voidColor: '#ff5a10' },
    start: ['img:surface-rock', W],
    checkpoint: ['metal', '#5a4a48'],
    end: ['img:surface-lava', W],
    blocks: [
      ['bricks', '#d0603a'],
      ['lava', '#ff7a2a'],
    ],
    solid: ['img:surface-cliff', '#8a6a5a'],
    extra: ['surf', 'updraft'],
    decor: 'volcano',
  },
  {
    name: 'bhop_abyss',
    title: 'Abyss',
    tier: 5,
    stages: 27,
    theme: { skyTop: '#000000', skyHorizon: '#141a3a', fog: '#080a18', ambient: 0.5, voidColor: '#04050c' },
    start: ['img:surface-cliff', '#6a7088'],
    checkpoint: ['crystal', '#5a3aa8'],
    end: ['crystal', '#21e1ff'],
    blocks: [
      ['crystal', '#7a5aff'],
      ['neon', '#21e1ff'],
    ],
    solid: ['img:surface-rock', '#5a6078'],
    layout: 'zig',
    extra: ['surf', 'blinkers'],
    decor: 'abyss',
  },
  {
    name: 'bhop_starship',
    title: 'Starship',
    tier: 5,
    stages: 28,
    theme: { skyTop: '#000004', skyHorizon: '#0c1a3a', fog: '#060c1c', ambient: 0.5, voidColor: '#04060c' },
    start: ['img:surface-metalplates', W],
    checkpoint: ['metal', '#5f6c82'],
    end: ['neon', '#3affb0'],
    blocks: [
      ['metal', '#9aa6ba'],
      ['neon', '#ffb03a'],
    ],
    solid: ['img:surface-paintedmetal', W],
    layout: 'arc',
    extra: ['surf', 'lowgrav'],
    decor: 'space',
  },
  {
    name: 'bhop_nightmare',
    title: 'Nightmare',
    tier: 5,
    stages: 28,
    theme: { skyTop: '#050008', skyHorizon: '#3a0a2a', fog: '#1a0414', ambient: 0.45, voidColor: '#100008' },
    start: ['img:surface-gravel', '#8a7a8a'],
    checkpoint: ['stone', '#5a4a5a'],
    end: ['lava', '#ff3a6a'],
    blocks: [
      ['bricks', '#8a3a5a'],
      ['stone', '#7a6a7a'],
    ],
    solid: ['img:surface-cliff', '#6a5a6a'],
    extra: ['surf', 'spinners'],
    decor: 'spooky',
  },
  {
    name: 'bhop_frozencore',
    title: 'Frozen Core',
    tier: 5,
    stages: 29,
    theme: { skyTop: '#6a9ac8', skyHorizon: '#e8f4ff', fog: '#d8e8f8', ambient: 0.6, voidColor: '#b8d4f0' },
    start: ['img:surface-ice', W],
    checkpoint: ['ice', '#a9d6ff'],
    end: ['crystal', '#bfe4ff'],
    blocks: [
      ['ice', '#9fd0ff'],
      ['crystal', '#a5a0ff'],
    ],
    solid: ['img:surface-snow', W],
    layout: 'zig',
    extra: ['surf', 'blinkers'],
    decor: 'winter',
  },
  {
    name: 'bhop_thunder',
    title: 'Thunder Road',
    tier: 5,
    stages: 29,
    theme: { skyTop: '#101420', skyHorizon: '#5a5a7a', fog: '#2a2a3a', ambient: 0.5, voidColor: '#1a1a24' },
    start: ['img:surface-concrete', W],
    checkpoint: ['metal', '#5a6272'],
    end: ['neon', '#ffe14a'],
    blocks: [
      ['metal', '#a8b0c0'],
      ['neon', '#ffe14a'],
    ],
    solid: ['img:surface-concrete', '#8a8a92'],
    extra: ['surf', 'windgap'],
    decor: 'skyline',
  },
  {
    name: 'bhop_neonhive',
    title: 'Neon Hive',
    tier: 5,
    stages: 30,
    theme: { skyTop: '#05030f', skyHorizon: '#2a0f45', fog: '#1a0b2e', ambient: 0.5, voidColor: '#0b0614' },
    start: ['img:surface-metalplates', '#8a8aa8'],
    checkpoint: ['neon', '#21e1ff'],
    end: ['neon', '#ff3fb4'],
    blocks: [
      ['neon', '#ff3fb4'],
      ['neon', '#9b5cff'],
    ],
    solid: ['metal', '#3a4256'],
    layout: 'arc',
    extra: ['surf', 'spinners'],
    decor: 'neon',
  },
  {
    name: 'bhop_summit',
    title: 'The Summit',
    tier: 5,
    stages: 31,
    theme: { skyTop: '#2a5aa8', skyHorizon: '#f0f6ff', fog: '#dce8f6', ambient: 0.6, voidColor: '#c8d8ea' },
    start: ['img:surface-snow', W],
    checkpoint: ['stone', '#8fa3b8'],
    end: ['crystal', '#bfe4ff'],
    blocks: [
      ['stone', '#a0aab8'],
      ['ice', '#c2e4ff'],
    ],
    solid: ['img:surface-cliff', W],
    extra: ['surf', 'pads'],
    decor: 'storm',
  },
  {
    name: 'bhop_apex',
    title: 'Apex',
    tier: 5,
    stages: 32,
    theme: { skyTop: '#0a0a0a', skyHorizon: '#5a1a1a', fog: '#2a0a0a', ambient: 0.5, voidColor: '#140404' },
    start: ['img:surface-metalplates', '#a8a8a8'],
    checkpoint: ['metal', '#6a5a5a'],
    end: ['neon', '#ff3a3a'],
    blocks: [
      ['metal', '#c09a88'],
      ['neon', '#ff5a3a'],
      ['crystal', '#ffa03a'],
    ],
    solid: ['img:surface-paintedmetal', '#8a7a7a'],
    layout: 'zig',
    extra: ['surf', 'lowgrav', 'blinkers'],
    decor: 'volcano',
  },
];

/**
 * Each tier's range, from its easiest map to its hardest, the way the first nine maps of that tier
 * were tuned: block size [first stage, last stage], fraction of the reachable gap used, design speed.
 */
const TIERS: { size: [number, number][]; reach: [number, number][]; top: [number, number][] }[] = [
  { size: [], reach: [], top: [] },
  {
    size: [
      [136, 120],
      [128, 112],
    ],
    reach: [
      [0.5, 0.6],
      [0.54, 0.64],
    ],
    top: [
      [300, 330],
      [310, 340],
    ],
  },
  {
    size: [
      [120, 104],
      [112, 96],
    ],
    reach: [
      [0.54, 0.66],
      [0.58, 0.7],
    ],
    top: [
      [320, 360],
      [330, 370],
    ],
  },
  {
    size: [
      [104, 88],
      [100, 84],
    ],
    reach: [
      [0.6, 0.72],
      [0.64, 0.76],
    ],
    top: [
      [340, 400],
      [350, 410],
    ],
  },
  {
    size: [
      [96, 80],
      [92, 76],
    ],
    reach: [
      [0.64, 0.77],
      [0.68, 0.8],
    ],
    top: [
      [360, 430],
      [370, 440],
    ],
  },
  {
    size: [
      [88, 68],
      [84, 64],
    ],
    reach: [
      [0.68, 0.8],
      [0.71, 0.83],
    ],
    top: [
      [380, 460],
      [385, 465],
    ],
  },
];
const lerp = (a: number, b: number, t: number) => a + (b - a) * t;
const between = (r: [number, number][], k: number): [number, number] => [
  lerp(r[0][0], r[1][0], k),
  lerp(r[0][1], r[1][1], k),
];
const round8 = (v: number) => Math.round(v / 8) * 8;

function recipe(s: Spec, i: number): Recipe {
  const inTier = SPECS.filter((x) => x.tier === s.tier);
  const k = inTier.indexOf(s) / Math.max(1, inTier.length - 1);
  const tier = TIERS[s.tier];
  const size = between(tier.size, k).map(round8) as [number, number];
  const solidLook = s.solid;
  return {
    name: s.name,
    title: s.title,
    difficulty: s.tier,
    stages: s.stages,
    seed: 101 + i * 13,
    theme: T(s.theme),
    start: s.start,
    checkpoint: s.checkpoint,
    end: s.end,
    blocks: s.blocks,
    solid: s.solid,
    size,
    reach: between(tier.reach, k),
    topSpeed: between(tier.top, k),
    modules: modules(s.tier, s.extra),
    layout: s.layout,
    exact: true,
    scenery: (sc: Scenery) => columns(sc, solidLook[0], solidLook[1], 900),
    decorate: DECOR[s.decor],
  };
}

export const NEW_RECIPES: Recipe[] = SPECS.map(recipe);
